// frontend/src/presentation/services/SpeechSynthesisService.ts
//
// Text-to-Speech dla asystenta "Woki-Toki" — czyta gotową odpowiedź AI na głos
// (docelowo do słuchawki Bluetooth operatora). Cienka otoczka na Web Speech API
// (`window.speechSynthesis`), izolująca warstwę prezentacji od globala i
// bezpieczna w środowiskach bez tego API (JSDOM, część przeglądarek).

/** Język lektora — odpowiedzi asystenta są po polsku. */
export const TTS_LANG = 'pl-PL'

/**
 * Domyślny limit bezpiecznika {@link SpeechSynthesisService.speakAndWait}. Silnik
 * TTS na Androidzie potrafi NIE oddać zdarzenia `onend` (zawiesza się przy
 * jednoczesnym starcie `MediaRecorder`/`getUserMedia`); po tylu ms `speakAndWait`
 * i tak kończy się sukcesem, żeby awaria lektora nie zablokowała nagrywania.
 */
export const DEFAULT_TTS_SAFEGUARD_MS = 1200

export class SpeechSynthesisService {
  /**
   * Czyta `text` na głos przez `window.speechSynthesis`. Tworzy
   * `SpeechSynthesisUtterance`, ustawia język na {@link TTS_LANG} i oddaje go
   * do kolejki mowy. Nowa wypowiedź przerywa poprzednią, żeby lektor się nie
   * nakładał przy szybkich pytaniach.
   *
   * No-op (bez wyjątku) dla pustego tekstu oraz gdy Web Speech API jest
   * niedostępne.
   *
   * @returns `true`, gdy wypowiedź trafiła do kolejki mowy; `false` w przeciwnym razie.
   */
  speak(text: string): boolean {
    const trimmed = text.trim()
    if (!trimmed) return false

    const synth: SpeechSynthesis | undefined = window.speechSynthesis
    const UtteranceCtor: typeof SpeechSynthesisUtterance | undefined =
      window.SpeechSynthesisUtterance

    if (!synth || !UtteranceCtor) {
      console.warn(
        '[SpeechSynthesisService] Web Speech API niedostępne — pomijam odczyt odpowiedzi AI.',
      )
      return false
    }

    synth.cancel()

    const utterance = new UtteranceCtor(trimmed)
    utterance.lang = TTS_LANG
    synth.speak(utterance)
    return true
  }

  /**
   * Jak {@link speak}, ale zwraca `Promise`, który rozwiązuje się, gdy lektor
   * SKOŃCZY mówić (`utterance.onend` / `onerror`) — albo, jako BEZPIECZNIK
   * (`Promise.race`), po `timeoutMs` (domyślnie {@link DEFAULT_TTS_SAFEGUARD_MS}),
   * cokolwiek nastąpi pierwsze.
   *
   * Nigdy nie odrzuca. Dla pustego tekstu / braku Web Speech API / wyjątku z
   * silnika rozwiązuje się natychmiast. Dzięki temu wywołujący może bezpiecznie
   * `await`-ować komunikat „Mów" i dopiero potem wystartować `MediaRecorder`, nie
   * ryzykując, że zawieszony silnik TTS zablokuje cały tor nagrywania.
   */
  async speakAndWait(text: string, options: { timeoutMs?: number } = {}): Promise<void> {
    const timeoutMs = options.timeoutMs ?? DEFAULT_TTS_SAFEGUARD_MS
    const trimmed = text.trim()

    const synth: SpeechSynthesis | undefined = window.speechSynthesis
    const UtteranceCtor: typeof SpeechSynthesisUtterance | undefined =
      window.SpeechSynthesisUtterance

    if (!trimmed || !synth || !UtteranceCtor) {
      if (trimmed) {
        console.warn(
          '[SpeechSynthesisService] Web Speech API niedostępne — pomijam komunikat głosowy.',
        )
      }
      return
    }

    const spoken = new Promise<void>((resolve) => {
      try {
        synth.cancel()
        const utterance = new UtteranceCtor(trimmed)
        utterance.lang = TTS_LANG
        utterance.onend = () => resolve()
        utterance.onerror = () => resolve()
        synth.speak(utterance)
      } catch (error) {
        console.warn(
          '[SpeechSynthesisService] Błąd silnika TTS — kończę bez czekania na lektora.',
          error,
        )
        resolve()
      }
    })

    let safeguardTimer: ReturnType<typeof setTimeout> | undefined
    const safeguard = new Promise<void>((resolve) => {
      safeguardTimer = setTimeout(resolve, timeoutMs)
    })

    try {
      await Promise.race([spoken, safeguard])
    } finally {
      if (safeguardTimer !== undefined) clearTimeout(safeguardTimer)
    }
  }

  /** Zatrzymuje bieżącą i zakolejkowane wypowiedzi (np. przy nowym pytaniu). */
  cancel(): void {
    window.speechSynthesis?.cancel()
  }
}

/** Współdzielona instancja serwisu TTS. */
export const speechSynthesisService = new SpeechSynthesisService()
